/**
 * AdminBusinessReview.jsx — Ops-only queue for verifying IFT business
 * listings through the 4-tier ladder.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ShieldCheck, XCircle } from "lucide-react";

const TIERS = ["unverified", "phone_verified", "documents_submitted", "field_verified"];
const TIER_LABEL = {
  unverified: "Unverified",
  phone_verified: "Phone-verified",
  documents_submitted: "Documents submitted",
  field_verified: "Verified (field)",
};

export default function AdminBusinessReview() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("pending_review");
  const [verification, setVerification] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const params = {};
      if (status) params.status = status;
      if (verification && verification !== "all") params.verification = verification;
      const { data } = await http.get("/admin/ift/businesses", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load queue");
      setItems([]);
    }
  }, [status, verification]);

  useEffect(() => { load(); }, [load]);

  const promote = async (bid, tier) => {
    const note = window.prompt(`Note for promoting to ${tier} (optional):`);
    setBusyId(bid);
    try {
      await http.patch(`/admin/ift/businesses/${bid}/verify`, { tier, note: note || null });
      toast.success(`Promoted to ${tier}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  };

  const suspend = async (bid) => {
    const reason = window.prompt("Suspend reason:");
    if (!reason) return;
    setBusyId(bid);
    try {
      await http.patch(`/admin/ift/businesses/${bid}/suspend`, { reason });
      toast.success("Suspended");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  };

  if (!user?.is_admin) return <div className="p-8 text-center text-sm opacity-70">Ops-only.</div>;

  return (
    <div className="max-w-4xl mx-auto py-8 px-4" data-testid="admin-biz-review">
      <div className="mb-6">
        <div className="overline">IFT · OPS · BUSINESS VERIFICATION QUEUE</div>
        <h1 className="font-display text-2xl">Business review</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border px-2 py-1" style={{ borderColor: "var(--wofy-line)" }} data-testid="admin-biz-status">
          <option value="pending_review">Pending review</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="all">All</option>
        </select>
        <select value={verification} onChange={(e) => setVerification(e.target.value)} className="rounded-md border px-2 py-1" style={{ borderColor: "var(--wofy-line)" }} data-testid="admin-biz-verification">
          <option value="all">All verification tiers</option>
          {TIERS.map((t) => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}
        </select>
      </div>

      {items === null ? (
        <div className="text-sm opacity-60"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <p className="text-sm opacity-70" data-testid="admin-biz-empty">Nothing in this filter.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((b) => (
            <li key={b.id} className="rounded-xl border p-4" style={{ borderColor: "var(--wofy-line)" }} data-testid={`admin-biz-row-${b.id}`}>
              <div className="flex flex-wrap justify-between gap-2">
                <div className="min-w-0">
                  <Link to={`/care/${b.business_slug}`} className="font-display text-lg underline underline-offset-2">{b.business_name}</Link>
                  <div className="text-xs opacity-70">
                    {b.business_category_label} · {b.primary_location?.city}, {b.primary_location?.country}
                    · {b.years_in_business || "—"}y
                    · GSTIN: {b.gstin || "—"}
                  </div>
                  <div className="text-xs mt-1">
                    <b>Verification:</b> {TIER_LABEL[b.verification_status]} · <b>Status:</b> {b.status}
                  </div>
                  {b.verification_documents?.length > 0 && (
                    <div className="text-xs mt-1 opacity-80">
                      Docs: {b.verification_documents.map((d) => <a key={d.file_url} href={d.file_url} target="_blank" rel="noreferrer" className="underline mr-2">{d.type}</a>)}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 items-end">
                  {TIERS.map((t) => t !== b.verification_status && (
                    <button
                      key={t}
                      onClick={() => promote(b.id, t)}
                      disabled={busyId === b.id}
                      className="wofy-btn-ghost text-[11px]"
                      data-testid={`admin-biz-promote-${b.id}-${t}`}
                    >
                      <ShieldCheck className="w-3 h-3" /> Set to {TIER_LABEL[t]}
                    </button>
                  ))}
                  {b.status !== "suspended" && (
                    <button onClick={() => suspend(b.id)} disabled={busyId === b.id} className="wofy-btn-ghost text-[11px]" style={{ color: "#B23A3A" }} data-testid={`admin-biz-suspend-${b.id}`}>
                      <XCircle className="w-3 h-3" /> Suspend
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
